import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { constants } from '../shared/constants/constants';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';
import { BaseService } from './base-service';
import { UserService } from './user.service';
import { ApplicationService } from './application.service';
import { DisaggregationResponseData } from '../shared/interfaces/plain-responses/disaggregation-response.interface';
import * as moment from 'moment';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
    providedIn: 'root',
})
export class DisaggregationService extends BaseService {
    private disaggregationData: any = null;

    constructor(
        protected http: HttpClient,
        protected auth: ApiService,
        protected user: UserService,
        private application: ApplicationService,
        private translate: TranslateService
    ) {
        super(http, auth, user);
    }

    destroy(): void {
        super.destroy();
    }

    /**
     * Requests the disaggregation data for the current month specifically for the tile
     */
    getDisaggregationDataForAppliancesTile(): Observable<any> {
        let data: Observable<any> = null;
        if (this.disaggregationData) {
            data = of(this.disaggregationData);
        } else {
            data = this.getDisaggregationHistoryNew(0);
        }
        return data.pipe(
            tap((receivedData) => (this.disaggregationData = receivedData)),
            mergeMap((receivedData) => this.mapForAppliancesTile(receivedData))
        );
    }

    /**
     * Get disaggregation data with a defined month offset
     * @param offset
     */
    getDisaggregationHistoryByOffset(offset: number): Observable<DisaggregationResponseData> {
        const currentLang = this.translate.currentLang;

        const method = moment()
            .locale(currentLang)
            .subtract(offset, 'months')
            .startOf('month')
            .format('[year]/YYYY/[month]/MM');
        return of(this.application.isDemoMode()).pipe(
            mergeMap((isDemoMode) => {
                if (isDemoMode) {
                    return this.getDisaggregationDemoData();
                }
                return this.requestDisaggregationHistory(method);
            })
        );
    }

    getDisaggregationHistoryForMonth(offset: number, month: number): Observable<any> {
        const currentLang = this.translate.currentLang;

        const method = `year/${moment()
            .locale(currentLang)
            .subtract(offset, 'years')
            .format('YYYY')}/month/${month}`;
        return of(this.application.isDemoMode()).pipe(
            mergeMap((isDemoMode) => {
                if (isDemoMode) {
                    return this.getDisaggregationDemoData();
                }
                return this.requestDisaggregationHistory(method);
            })
        );
    }

    private getDisaggregationHistoryNew(offset: number, month = 0): Observable<any> {
        const date = new Date();
        date.setMonth(date.getMonth() - offset, 1);

        let method = `year/${date.getFullYear()}/month/${date.getMonth() + 1}`;
        if (month > 0) {
            method = `year/${date.getFullYear()}/month/${month}`;
        }

        let url = this.API_BASE_URL + constants.api.routes.disaggregation + '/' + method;
        if (this.application.isDemoMode()) {
            url = `assets/data/demo/${constants.demo.files.disaggregationYearMonth}.json`;
        }

        return this.http.get(url).pipe(
            map((res: { status: string; data: any }) => this.mapDefault(res)),
            map((disaggregationData: any) => {
                if (this.application.isDemoMode()) {
                    return disaggregationData;
                } else {
                    return disaggregationData;
                }
            }),
            catchError((error: any) => this.handleError(error))
        );
    }

    /**
     * Main method to get disaggregation history
     * @param method
     * @private
     */
    private requestDisaggregationHistory(method: string): Observable<DisaggregationResponseData> {
        const url = this.API_BASE_URL + constants.api.routes.disaggregation + '/' + method;
        return this.http.get(url).pipe(
            map((res: { status: string; data: any }) => {
                return this.mapDefault(res);
            }),
            catchError((error: any) => this.handleError(error))
        );
    }

    /**
     * Get disaggregation demo data
     * @private
     */
    private getDisaggregationDemoData(): Observable<DisaggregationResponseData> {
        const url = `assets/data/demo/${constants.demo.files.disaggregationYearMonth}.json`;
        return this.http.get(url).pipe(map((res) => this.mapDefault(res)));
    }

    /**
     * Map response for appliances tile
     * @param response
     * @private
     */
    private mapForAppliancesTile(response: any): Observable<any> {
        try {
            return of(response?.electricity?.categories);
        } catch (e) {
            return throwError(e);
        }
    }
}
